// TUT-450 — o financeiro: contas a pagar (faixas, vencimento, pagamento, relatório técnico, NF) e contas a receber.
import { demoApi, demoSession } from "../demo-data";
import { hojeLocal, montarListaDeContasAPagar } from "../contas-a-pagar";
import { contaAReceberEncerrada, hojeNoEscritorio, montarListaDeContasAReceber } from "../contas-a-receber";
import { enviarAoArmazenamento, type RelatorDeProgresso } from "../upload-transport";
import { ApiError } from "../api-error";
import { demoAccountsPayable, demoAccountsReceivable, demoLocalRepresentatives, request, sha256 } from "./infra";
import { demoSuppliers } from "../demo/fornecedores";
import { recusaDaTransicaoDoRecebivel, recusaDoDesfazer, recusaDoLimiteDoVencimento, recusaDoRecebimento, recusaDoVencimento } from "../demo/lotes";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDoFinanceiro = {
  /** O conjunto inteiro, sem página: o quadro do Financeiro e a pilha de pop-ups. */
  accountsPayable(includeClosed = false) {
    if (useDemoApi) return Promise.resolve({ items: demoAccountsPayable.filter((item) => includeClosed || !["PAID", "CANCELLED"].includes(item.status)) });
    return request<{ items: import("../types").AccountsPayable[] }>(`/accounts-payable${includeClosed ? "?includeClosed=true" : ""}`);
  },
  /** TUT-334 — a lista da tela: faixas, busca, ordem e página no servidor. */
  accountsPayableList(query: import("../types").AccountsPayableListQuery) {
    if (useDemoApi) {
      return Promise.resolve(montarListaDeContasAPagar(demoAccountsPayable, { ...query, hoje: hojeLocal() }));
    }
    const params = new URLSearchParams();
    // Chave repetida (`faixa=a&faixa=b`): "ou" dentro do campo, "e" entre campos.
    for (const faixa of query.faixa ?? []) params.append("faixa", faixa);
    for (const id of query.representante ?? []) params.append("representativeId", id);
    if (query.search?.trim()) params.set("search", query.search.trim());
    if (query.processo?.trim()) params.set("processNumber", query.processo.trim());
    if (query.de) params.set("dueFrom", query.de);
    if (query.ate) params.set("dueTo", query.ate);
    if (query.valorMin !== undefined) params.set("amountMinCents", String(query.valorMin));
    if (query.valorMax !== undefined) params.set("amountMaxCents", String(query.valorMax));
    if (query.sort) params.set("sort", query.sort);
    if (query.dir) params.set("dir", query.dir);
    if (query.page) params.set("page", String(query.page));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    return request<import("../types").AccountsPayableListResponse>(`/accounts-payable?${params}`);
  },
  /** TUT-334 — "Definir vencimento". */
  setAccountsPayableDueDate(id: string, dueDate: string) {
    if (useDemoApi) {
      const item = demoAccountsPayable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Pagar não encontrada."));
      if (item.status === "PAID" || item.status === "CANCELLED") return Promise.reject(new Error("Conta a Pagar encerrada não muda de vencimento."));
      item.dueDate = dueDate;
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsPayable>(`/accounts-payable/${encodeURIComponent(id)}/due-date`, { method: "PATCH", body: JSON.stringify({ dueDate }) });
  },
  /** TUT-334 — "Marcar como pagas", em lote, com o dia do pagamento. */
  payAccountsPayable(ids: string[], paidOn: string) {
    if (useDemoApi) {
      const items: import("../types").ResultadoDoPagamento[] = ids.map((id) => {
        const item = demoAccountsPayable.find((candidate) => candidate.id === id);
        if (!item) return { id, ok: false, motivo: "Conta a Pagar não encontrada." };
        if (item.status !== "READY_FOR_PAYMENT") return { id, ok: false, motivo: "Ainda aguarda o relatório técnico ou a NF/recibo, ou já está encerrada." };
        item.status = "PAID";
        item.paidAt = `${paidOn}T12:00:00.000Z`;
        item.paidByName = demoSession.user.name;
        return { id, ok: true };
      });
      return Promise.resolve({ items });
    }
    return request<{ items: import("../types").ResultadoDoPagamento[] }>(`/accounts-payable/pay`, { method: "POST", body: JSON.stringify({ ids, paidOn }) });
  },
  async accountsPayableTechnicalReportCandidates(id: string) {
    if (useDemoApi) {
      const payable = demoAccountsPayable.find((candidate) => candidate.id === id);
      if (!payable) throw new Error("Conta a Pagar não encontrada.");
      const documents = await demoApi.documents("", { page: 1, pageSize: 100 });
      return {
        items: documents.items
          .filter((document) => document.processId === payable.processId && document.category.key === "technical_report")
          .flatMap((document) => document.versions
            .filter((version) => version.status.key === "available")
            .map((version) => ({ documentVersionId: version.id, title: document.title, originalName: version.originalName }))),
      };
    }
    return request<{ items: import("../types").AccountsPayableTechnicalReportCandidate[] }>(
      `/accounts-payable/${encodeURIComponent(id)}/technical-report-candidates`,
    );
  },
  attachAccountsPayableTechnicalReport(id: string, documentVersionId: string) {
    if (useDemoApi) {
      const item = demoAccountsPayable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Pagar não encontrada."));
      item.deliverables.technicalReport = { id: crypto.randomUUID(), documentVersionId };
      if (item.deliverables.invoiceReceipt) item.status = "READY_FOR_PAYMENT";
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsPayable>(`/accounts-payable/${encodeURIComponent(id)}/technical-report`, { method: "POST", body: JSON.stringify({ documentVersionId }) });
  },
  /** TUT-334 — `dueDate` obrigatório: o vencimento é escolhido no anexo da NF. */
  async uploadAccountsPayableInvoiceReceipt(id: string, file: File, dueDate: string, aoProgredir?: RelatorDeProgresso) {
    if (useDemoApi) {
      const item = demoAccountsPayable.find((candidate) => candidate.id === id);
      if (!item) throw new Error("Conta a Pagar não encontrada.");
      item.deliverables.invoiceReceipt = { id: crypto.randomUUID(), originalName: file.name };
      item.dueDate = dueDate;
      if (item.deliverables.technicalReport) item.status = "READY_FOR_PAYMENT";
      return item;
    }
    aoProgredir?.({ fase: "preparando" });
    const prepared = await request<{ uploadId: string; upload: { url: string; method: "PUT"; headers: Record<string, string> } }>(`/accounts-payable/${encodeURIComponent(id)}/invoice-receipt/uploads`, {
      method: "POST", body: JSON.stringify({ originalName: file.name, mimeType: file.type, sizeBytes: file.size, checksumSha256: await sha256(file) }),
    });
    const response = await enviarAoArmazenamento({ url: prepared.upload.url, method: prepared.upload.method, headers: prepared.upload.headers, corpo: file, aoProgredir });
    if (!response.ok) throw new ApiError("O armazenamento recusou o arquivo.", response.status);
    aoProgredir?.({ fase: "concluindo" });
    return request(`/accounts-payable/invoice-receipt/uploads/${encodeURIComponent(prepared.uploadId)}/complete`, { method: "POST", body: JSON.stringify({ dueDate }) });
  },
  transitionAccountsPayable(id: string, to: "PAID" | "CANCELLED", reason?: string) {
    if (useDemoApi) {
      const item = demoAccountsPayable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Pagar não encontrada."));
      item.status = to; item.cancellationReason = to === "CANCELLED" ? reason ?? null : null;
      item.paidAt = to === "PAID" ? new Date().toISOString() : null;
      item.paidByName = to === "PAID" ? demoSession.user.name : null;
      item.cancelledAt = to === "CANCELLED" ? new Date().toISOString() : null;
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsPayable>(`/accounts-payable/${encodeURIComponent(id)}/status`, { method: "PATCH", body: JSON.stringify({ to, ...(reason ? { reason } : {}) }) });
  },
  accountsReceivable(includeClosed = false) {
    if (useDemoApi) {
      return Promise.resolve({
        items: demoAccountsReceivable.filter(
          // TUT-506 — a faturada espera o dinheiro: encerradas são a recebida e a cancelada.
          (item) => includeClosed || !contaAReceberEncerrada(item.status),
        ),
      });
    }
    return request<{ items: import("../types").AccountsReceivable[] }>(
      `/accounts-receivable${includeClosed ? "?includeClosed=true" : ""}`,
    );
  },
  /** TUT-483 — a lista da tela: busca, filtros avançados, ordem e página no servidor. */
  accountsReceivableList(query: import("../types").AccountsReceivableListQuery) {
    if (useDemoApi) return Promise.resolve(montarListaDeContasAReceber(demoAccountsReceivable, { ...query, hoje: hojeNoEscritorio() }));
    const params = new URLSearchParams();
    for (const status of query.status ?? []) params.append("status", status);
    // TUT-506 — a faixa do vencimento e o período dele.
    for (const faixa of query.vencimento ?? []) params.append("dueBand", faixa);
    if (query.de) params.set("dueFrom", query.de);
    if (query.ate) params.set("dueTo", query.ate);
    for (const cliente of query.cliente ?? []) params.append("clientId", cliente);
    for (const marco of query.marco ?? []) params.append("stage", marco);
    for (const rota of query.rota ?? []) params.append("route", rota);
    if (query.search?.trim()) params.set("search", query.search.trim());
    if (query.processo?.trim()) params.set("processNumber", query.processo.trim());
    if (query.valorMin !== undefined) params.set("amountMinCents", String(query.valorMin));
    if (query.valorMax !== undefined) params.set("amountMaxCents", String(query.valorMax));
    if (query.sort) params.set("sort", query.sort);
    if (query.dir) params.set("dir", query.dir);
    if (query.page) params.set("page", String(query.page));
    if (query.pageSize) params.set("pageSize", String(query.pageSize));
    return request<import("../types").AccountsReceivableListResponse>(`/accounts-receivable?${params}`);
  },
  satisfyAccountsReceivable(
    id: string,
    input: { purchaseOrderIdentifier?: string; manualRelease?: boolean },
  ) {
    if (useDemoApi) {
      const item = demoAccountsReceivable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Receber não encontrada."));
      if (input.purchaseOrderIdentifier) item.purchaseOrderIdentifier = input.purchaseOrderIdentifier;
      if (input.manualRelease) item.manualReleasedAt = new Date().toISOString();
      if (
        (!item.requirements.purchaseOrderIdentifier || item.purchaseOrderIdentifier) &&
        (!item.requirements.platformEvidence || item.evidence.platform) &&
        (!item.requirements.manualRelease || item.manualReleasedAt)
      ) item.status = "READY_TO_INVOICE";
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsReceivable>(
      `/accounts-receivable/${encodeURIComponent(id)}/requirements`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  },
  async uploadAccountsReceivableEvidence(
    id: string,
    kind: "PLATFORM_EVIDENCE" | "ISSUANCE_PROOF",
    file: File,
    aoProgredir?: RelatorDeProgresso,
  ) {
    if (useDemoApi) {
      const item = demoAccountsReceivable.find((candidate) => candidate.id === id);
      if (!item) throw new Error("Conta a Receber não encontrada.");
      const evidence = { id: crypto.randomUUID(), originalName: file.name };
      if (kind === "PLATFORM_EVIDENCE") item.evidence.platform = evidence;
      else item.evidence.issuanceProof = evidence;
      return item;
    }
    aoProgredir?.({ fase: "preparando" });
    const prepared = await request<{
      uploadId: string;
      upload: { url: string; method: "PUT"; headers: Record<string, string> };
    }>(
      `/accounts-receivable/${encodeURIComponent(id)}/evidence/${kind}/uploads`,
      {
        method: "POST",
        body: JSON.stringify({
          originalName: file.name,
          mimeType: file.type,
          sizeBytes: file.size,
          checksumSha256: await sha256(file),
        }),
      },
    );
    const response = await enviarAoArmazenamento({
      url: prepared.upload.url,
      method: prepared.upload.method,
      headers: prepared.upload.headers,
      corpo: file,
      aoProgredir,
    });
    if (!response.ok) throw new ApiError("O armazenamento recusou o arquivo.", response.status);
    aoProgredir?.({ fase: "concluindo" });
    return request<import("../types").AccountsReceivable>(
      `/accounts-receivable/evidence/uploads/${encodeURIComponent(prepared.uploadId)}/complete`,
      { method: "POST" },
    );
  },
  /** Faturar (TUT-506: com o vencimento, obrigatório) e cancelar (com o motivo). */
  transitionAccountsReceivable(
    id: string,
    to: "INVOICED" | "CANCELLED",
    reason?: string,
    dueDate?: string,
  ) {
    if (useDemoApi) {
      const item = demoAccountsReceivable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Receber não encontrada."));
      // TUT-498 — a regra de liberação da API (`assertReceivableTransition`):
      // a demonstração faturava qualquer conta, e o lote a mediu.
      const recusa = recusaDaTransicaoDoRecebivel(item, to, reason);
      if (recusa) return Promise.reject(new Error(recusa));
      if (to === "INVOICED" && !dueDate) return Promise.reject(new Error("Informe o vencimento para faturar."));
      const foraDoLimite = to === "INVOICED" && dueDate ? recusaDoLimiteDoVencimento(dueDate, hojeNoEscritorio()) : null;
      if (foraDoLimite) return Promise.reject(new Error(foraDoLimite));
      item.status = to;
      item.invoicedAt = to === "INVOICED" ? new Date().toISOString() : null;
      item.dueDate = to === "INVOICED" ? (dueDate ?? null) : null;
      item.cancelledAt = to === "CANCELLED" ? new Date().toISOString() : null;
      item.cancellationReason = to === "CANCELLED" ? reason ?? null : null;
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsReceivable>(
      `/accounts-receivable/${encodeURIComponent(id)}/status`,
      { method: "PATCH", body: JSON.stringify({ to, ...(reason ? { reason } : {}), ...(dueDate ? { dueDate } : {}) }) },
    );
  },
  /** TUT-506 — "Alterar vencimento", só da conta faturada. */
  setAccountsReceivableDueDate(id: string, dueDate: string) {
    if (useDemoApi) {
      const item = demoAccountsReceivable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Receber não encontrada."));
      const recusa = recusaDoVencimento(item, dueDate);
      if (recusa) return Promise.reject(new Error(recusa));
      item.dueDate = dueDate;
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsReceivable>(`/accounts-receivable/${encodeURIComponent(id)}/due-date`, {
      method: "PATCH",
      body: JSON.stringify({ dueDate }),
    });
  },
  /** TUT-506 — "Registrar recebimento": a baixa manual, do valor integral, no dia escolhido. */
  registerAccountsReceivableReceipt(id: string, receivedOn: string) {
    if (useDemoApi) {
      const item = demoAccountsReceivable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Receber não encontrada."));
      const recusa = recusaDoRecebimento(item, receivedOn, hojeNoEscritorio());
      if (recusa) return Promise.reject(new Error(recusa));
      item.status = "RECEIVED";
      item.receivedAt = `${receivedOn}T03:00:00.000Z`;
      item.receivedAmountCents = item.amountCents;
      item.receiptOrigin = "MANUAL";
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsReceivable>(`/accounts-receivable/${encodeURIComponent(id)}/receipt`, {
      method: "POST",
      body: JSON.stringify({ receivedOn }),
    });
  },
  /** TUT-506 — "Desfazer recebimento": volta para Faturado, com motivo auditado. */
  undoAccountsReceivableReceipt(id: string, reason: string) {
    if (useDemoApi) {
      const item = demoAccountsReceivable.find((candidate) => candidate.id === id);
      if (!item) return Promise.reject(new Error("Conta a Receber não encontrada."));
      const recusa = recusaDoDesfazer(item, reason);
      if (recusa) return Promise.reject(new Error(recusa));
      item.status = "INVOICED";
      item.receivedAt = null;
      item.receivedAmountCents = null;
      item.receiptOrigin = null;
      return Promise.resolve(item);
    }
    return request<import("../types").AccountsReceivable>(`/accounts-receivable/${encodeURIComponent(id)}/receipt/undo`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    });
  },
  /**
   * TUT-585 — quem pode ser o favorecido da conta a pagar avulsa: os
   * fornecedores ativos e os assistentes externos (Q13).
   */
  accountsPayablePayees(): Promise<import("../types").AccountsPayablePayees> {
    if (useDemoApi) {
      // Os assistentes da demonstração: os cadastrados (quando há) e os que as
      // contas semeadas já citam, para a lista nascer com gente.
      const porId = new Map<string, string>();
      for (const item of demoLocalRepresentatives) porId.set(item.id, item.name);
      for (const conta of demoAccountsPayable) {
        if (conta.representativeId && !porId.has(conta.representativeId)) porId.set(conta.representativeId, conta.payeeName);
      }
      return Promise.resolve({
        suppliers: demoSuppliers
          .filter((fornecedor) => fornecedor.active)
          .map((fornecedor) => ({ id: fornecedor.id, name: fornecedor.name, document: fornecedor.document }))
          .sort(porNome),
        representatives: [...porId].map(([id, name]) => ({ id, name })).sort(porNome),
      });
    }
    return request<import("../types").AccountsPayablePayees>("/accounts-payable/payees");
  },
  /** TUT-585 — "Nova conta a pagar": a conta avulsa nasce pronta para pagar, com vencimento. */
  async createAccountsPayable(input: import("../types").CreateAccountsPayableInput): Promise<import("../types").AccountsPayable> {
    if (useDemoApi) {
      const recusa = recusaDaContaAvulsa(input);
      if (recusa) throw new Error(recusa);
      if (Boolean(input.supplierId) === Boolean(input.representativeId)) throw new Error("Escolha o favorecido da conta.");
      const fornecedor = input.supplierId ? demoSuppliers.find((item) => item.id === input.supplierId) : undefined;
      const assistente = input.representativeId ? (await apiDoFinanceiro.accountsPayablePayees()).representatives.find((item) => item.id === input.representativeId) : undefined;
      if (!fornecedor && !assistente) throw new Error("Favorecido não encontrado.");
      const vinculo = await vinculoDaContaAvulsa(input);
      const agora = new Date().toISOString();
      const criada: import("../types").AccountsPayable = {
        id: crypto.randomUUID(),
        expertiseId: input.expertiseId ?? null,
        processId: vinculo.processId,
        processNumber: vinculo.processNumber,
        representativeId: assistente?.id ?? null,
        representativeName: assistente?.name ?? null,
        contractedAmountCents: input.amountCents,
        excessKmAmountCents: 0,
        paymentTerms: null,
        origin: "MANUAL",
        description: input.description.trim(),
        notes: input.notes?.trim() || null,
        supplierId: fornecedor?.id ?? null,
        payeeName: fornecedor?.name ?? assistente?.name ?? "",
        payeeKind: fornecedor ? "SUPPLIER" : "REPRESENTATIVE",
        status: "READY_FOR_PAYMENT",
        dueDate: input.dueDate,
        deliverables: { technicalReport: null, invoiceReceipt: null },
        cancellationReason: null,
        paidAt: null,
        paidByName: null,
        cancelledAt: null,
        createdAt: agora,
        updatedAt: agora,
      };
      demoAccountsPayable.push(criada);
      return criada;
    }
    return request<import("../types").AccountsPayable>("/accounts-payable", { method: "POST", body: JSON.stringify(input) });
  },
  /** TUT-585 — "Nova conta a receber": nasce FATURADA, com o vencimento, esperando o dinheiro (Q12: o cliente cadastrado é obrigatório). */
  async createAccountsReceivable(input: import("../types").CreateAccountsReceivableInput): Promise<import("../types").AccountsReceivable> {
    if (useDemoApi) {
      const recusa = recusaDaContaAvulsa(input);
      if (recusa) throw new Error(recusa);
      const cliente = (await demoApi.organizations("")).items.find((item) => item.id === input.payerOrganizationId);
      if (!cliente) throw new Error("Escolha um cliente cadastrado como pagador.");
      const vinculo = await vinculoDaContaAvulsa(input);
      const agora = new Date().toISOString();
      const criada: import("../types").AccountsReceivable = {
        id: crypto.randomUUID(),
        contractId: null,
        serviceOrderId: null,
        processId: vinculo.processId,
        processNumber: vinculo.processNumber,
        origin: "MANUAL",
        description: input.description.trim(),
        notes: input.notes?.trim() || null,
        organizationName: cliente.name,
        organizationId: cliente.id,
        stage: null,
        sourceKind: null,
        occurrence: 1,
        amountCents: input.amountCents,
        route: "DIRECT",
        requirements: { purchaseOrderIdentifier: false, platformEvidence: false, manualRelease: false },
        purchaseOrderIdentifier: null,
        manualReleasedAt: null,
        status: "INVOICED",
        evidence: { platform: null, issuanceProof: null },
        invoicedAt: agora,
        dueDate: input.dueDate,
        receivedAt: null,
        receivedAmountCents: null,
        receiptOrigin: null,
        cancellationReason: null,
        cancelledAt: null,
        createdAt: agora,
        updatedAt: agora,
      };
      demoAccountsReceivable.push(criada);
      return criada;
    }
    return request<import("../types").AccountsReceivable>("/accounts-receivable", { method: "POST", body: JSON.stringify(input) });
  },
};

const porNome = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, "pt-BR");

/** TUT-585 — as recusas de formato que a API também faz (descrição, valor e vencimento). */
function recusaDaContaAvulsa(input: { description: string; amountCents: number; dueDate: string }): string | null {
  const descricao = input.description.trim();
  if (descricao.length < 2 || descricao.length > 200) return "A descrição deve ter de 2 a 200 caracteres.";
  if (!Number.isInteger(input.amountCents) || input.amountCents < 1) return "Informe um valor maior que zero.";
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(input.dueDate)) return "Informe o vencimento.";
  return null;
}

/** TUT-585 — o vínculo opcional: com a perícia o processo é o dela (como na API). */
async function vinculoDaContaAvulsa(input: { processId?: string | undefined; expertiseId?: string | undefined }): Promise<{ processId: string | null; processNumber: string | null }> {
  if (input.expertiseId) {
    const pericia = (await demoApi.expertises({ pageSize: 100 })).items.find((item) => item.id === input.expertiseId);
    if (!pericia) throw new Error("Perícia não encontrada.");
    return { processId: pericia.processId, processNumber: pericia.processNumber };
  }
  if (input.processId) {
    const processo = (await demoApi.processes({ page: 1, pageSize: 100 })).items.find((item) => item.id === input.processId);
    if (!processo) throw new Error("Processo não encontrado.");
    return { processId: processo.id, processNumber: processo.number };
  }
  return { processId: null, processNumber: null };
}
